(() => {
  if (customElements.get('seat-guide')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const unique = values => [...new Set(values)].sort((a,b)=>a.localeCompare(b,document.documentElement.lang,{numeric:true}));
  class SeatGuide extends HTMLElement {
    connectedCallback() {
      if(this.started)return;
      this.started=true;
      this.labels=JSON.parse(this.querySelector('[data-labels]').textContent);
      this.fields=Object.fromEntries(['make','model','year'].map(k=>[k,this.querySelector(`[data-field="${k}"]`)]));
      this.results=this.querySelector('[data-results]');
      this.status=this.querySelector('[data-status]');
      this.showResults=this.querySelector('[data-show-results]');
      this.showResults.addEventListener('click',()=>{
        if(this.showResults.disabled)return;
        this.status.focus({preventScroll:true});
        this.status.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
      });
      this.revision=0;
      this.init();
    }
    async init() {
      try {
        const response=await fetch(this.dataset.source);
        if(!response.ok)throw new Error('Data unavailable');
        this.data=await response.json();
        this.terms=this.data.translations?.[document.documentElement.lang.split('-')[0]]||{};
        this.fill('make',unique(this.data.fitments.map(r=>r.make)));
        this.fields.make.disabled=false;
        this.addEventListener('change',e=>{if(e.target.matches('[data-field]'))this.change(e.target.dataset.field);});
        this.querySelector('[data-reset]').addEventListener('click',()=>{this.fields.make.value='';this.change('make');this.fields.make.focus();});
        this.status.textContent=this.labels.start;
      } catch {this.status.textContent=this.labels.error;}
    }
    fill(key,values) {
      this.fields[key].replaceChildren(new Option(this.labels['choose_'+key],''),...values.map(v=>new Option(this.localize(v),v)));
    }
    localize(value){return this.terms?.[value]||value;}
    choice(){return Object.fromEntries(Object.entries(this.fields).map(([k,f])=>[k,f.value]));}
    change(key) {
      if(key==='make'){
        this.fill('model',unique(this.data.fitments.filter(r=>r.make===this.fields.make.value).flatMap(r=>r.models)));
        this.fields.model.disabled=!this.fields.make.value;
      }
      if(key==='make'||key==='model'){
        this.fill('year',SeatEngine.availableYears(this.data,this.fields.make.value,this.fields.model.value).map(String));
        if(this.fields.model.value)this.fields.year.add(new Option(this.labels.unspecified,'all'));
        this.fields.year.disabled=!this.fields.model.value;
      }
      this.showResults.disabled=!Object.values(this.choice()).every(Boolean);
      this.render();
    }
    period(r) {
      const fmt=d=>String(d.year)+(d.month?'-'+String(d.month).padStart(2,'0'):'');
      if(r.allYears)return this.labels.all_years;
      if(r.start&&r.end)return `${fmt(r.start)} – ${fmt(r.end)}`;
      if(r.start)return `${fmt(r.start)} – ${this.labels.unknown.toLowerCase()}`;
      if(r.end)return `${this.labels.unknown} – ${fmt(r.end)}`;
      return this.labels.unknown;
    }
    needsReview(r){return r.flags.some(f=>/conflict|needs_review|duplicate_right/.test(f));}
    specs(r) {
      const l=this.labels;
      const rail=[r.rail==='no'?l.rail_no:r.rail,/^serie$/i.test(r.railDetail)?l.rail_original:/^no$/i.test(r.railDetail)?l.rail_no:r.railDetail].filter(Boolean).join(' / ')||l.rail_unknown;
      const mounting=/QRT/i.test(r.note)?l.qrt:/ultra/i.test(r.modelDetail+' '+r.series)?l.ultra:r.note;
      const series=this.localize(r.series).replace('(per subwoofer)',`(${l.subwoofer})`);
      const material=/^Fe\s*\d+$/i.test(r.material)?l.material_steel:/^Al$/i.test(r.material)?l.material_aluminium:r.material;
      const approval=/^no$/i.test((r.approval||'').trim())?'':this.localize(r.approval);
      const values=[['part',r.mpn],['side',l[r.side]||l.side_unknown],['period',this.period(r)],['series',series],['vehicle_detail',this.localize(r.modelDetail)],['mounting',mounting],['rail',rail],['material',material],['dimensions',r.dimensions],['approval',approval]];
      const notices=[r.boundary?l.boundary:'',r.incomplete&&r.start&&!r.end?l.open_period:''].filter(Boolean);
      return `<table class="body-small"><tbody>${values.filter(([,v])=>v).map(([k,v])=>`<tr><th scope="row">${escape(l[k])}</th><td>${escape(v)}</td></tr>`).join('')}</tbody></table>${notices.map(n=>`<p class="seat-guide__notice body-small">${escape(n)}</p>`).join('')}`;
    }
    async render() {
      const revision=++this.revision,c=this.choice();
      this.results.replaceChildren();this.results.removeAttribute('aria-busy');
      if(!c.make||!c.model||!c.year){this.status.textContent=this.labels.start;return;}
      const groups=new Map();
      for(const r of SeatEngine.assess(this.data,c)){if(!groups.has(r.mpn))groups.set(r.mpn,[]);groups.get(r.mpn).push(r);}
      this.status.textContent=groups.size?(groups.size===1?this.labels.results_one:this.labels.results).replace('[count]',groups.size):this.labels.empty;
      this.results.setAttribute('aria-busy','true');
      await Promise.all([...groups].map(async([mpn,rows])=>{
        const article=document.createElement('article');article.className='seat-guide__result';
        const r=rows[0],l=this.labels;
        const extra=/subwoofer/i.test(r.series)?l.subwoofer:/QRT/i.test(r.note)?l.qrt:/ultra/i.test(r.modelDetail+' '+r.series)?l.ultra:/adapter/i.test(r.modelDetail)?l.adapter:'';
        article.innerHTML=`<header class="seat-guide__result-header"><h2 class="body-large">${escape(c.make)} ${escape(this.localize(c.model))} · ${escape(l[r.side]||l.side_unknown)}${extra?' · '+escape(extra):''}</h2></header><div class="seat-guide__result-body"><div class="seat-guide__specs">${rows.map(row=>this.specs(row)).join('')}</div><div class="seat-guide__purchase" data-purchase><p>${escape(l.loading)}</p></div></div>`;
        this.results.append(article);
        const slot=article.querySelector('[data-purchase]'),product=this.data.products[mpn];
        if(this.hasAttribute('data-preview')){
          slot.innerHTML=`<div><p>${escape(l.product_unavailable)} ${escape(mpn)}</p><p class="body-small">${escape(l.preview)}</p>${product?`<a href="https://www.radne.se/products/${encodeURIComponent(product.handle)}" target="_blank" rel="noopener">${escape(l.view_product)}</a>`:''}</div>`;
          return;
        }
        try {
          if(!product?.handle)throw new Error('No storefront mapping');
          const url=new URL(`${window.Shopify?.routes?.root||'/'}products/${encodeURIComponent(product.handle)}`,location.origin);
          url.searchParams.set('view','seat-guide');url.searchParams.set('variant',product.variantId);
          const response=await fetch(url);if(!response.ok)throw new Error('Product unavailable');
          const doc=new DOMParser().parseFromString(await response.text(),'text/html');
          const card=doc.querySelector('[data-seat-product]');
          if(!card||card.dataset.variantId!==product.variantId||card.dataset.mpn.trim().toUpperCase()!==mpn)throw new Error('Mapping changed');
          if(revision!==this.revision)return;
          card.querySelectorAll('form[id]').forEach(form=>{
            const old=form.getAttribute('id'),next=`${this.id}-${mpn}-${old}`;form.setAttribute('id',next);
            card.querySelectorAll('[form]').forEach(el=>{if(el.getAttribute('form')===old)el.setAttribute('form',next);});
          });
          if(rows.some(row=>this.needsReview(row)))card.querySelectorAll('quick-add-product,.product-item-tabular-qty-with-buy-button').forEach(el=>el.remove());
          slot.replaceChildren(document.importNode(card,true));
        } catch {
          if(revision!==this.revision)return;
          slot.innerHTML=`<div><p>${escape(l.unavailable)}</p><a class="btn btn--text" href="${escape(this.dataset.contact)}">${escape(l.contact)}</a></div>`;
        }
      }));
      if(revision===this.revision)this.results.removeAttribute('aria-busy');
    }
  }
  customElements.define('seat-guide',SeatGuide);
})();
